import type { SessionRefusalReason } from '@accounts/shared';

import { LOGIN_PATH } from './surfaces';

/**
 * WHY THE SIGN-IN PAGE SOMETIMES SAYS MORE — R4, extended by C3.
 *
 * The server's 401 stays uniform for an anonymous caller: a missing cookie, a forged
 * signature, a malformed payload and an unknown user id are all indistinguishable, because
 * anything else turns the 401 into an account-enumeration oracle.
 *
 * TWO facts decide what the sign-in page says, and both are safe:
 *
 *   • PROVENANCE, which only the client knows — an anonymous caller who typed the wrong
 *     credentials gets the generic message, always; a client that HELD a live session and
 *     had it refused knows its session was ended for it.
 *   • The server's `reason`, which is attached ONLY when the presented cookie unsigned
 *     validly and named a real account — i.e. only to somebody this service already issued a
 *     session to. It cannot be provoked by a caller who does not hold one.
 *
 * Both ride `sessionStorage` (not the URL) so neither can be forged by a link, and both are
 * consumed exactly once by the sign-in page.
 */

export type BounceKind =
  /** The person pressed log out. */
  | 'signed-out'
  /** A live session was refused by the server. */
  | 'session-refused';

export interface Bounce {
  readonly kind: BounceKind;
  /** Present only when the server named one. Absent means "the session simply lapsed". */
  readonly reason: SessionRefusalReason | null;
}

const REFUSAL_REASONS: readonly SessionRefusalReason[] = [
  'account_inactive',
  'signed_out_elsewhere',
  'password_changed',
  'session_ended',
  'elevation_required',
];

const STORAGE_KEY = 'accounts.bounce';

/** True once `GET /api/auth/me` has succeeded — i.e. we are holding a live session. */
let sessionEstablished = false;

/**
 * What to do the moment a live session is refused. Registered once at the app root; without
 * it a session that dies mid-visit would leave the person sitting on a rendered admin screen
 * whose reads all 403/401 — the empty-shell failure this app refuses to ship.
 */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

/** Serialised as `kind` or `kind:reason` — one key, so one read consumes both facts. */
function write(kind: BounceKind, reason?: SessionRefusalReason): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, reason === undefined ? kind : `${kind}:${reason}`);
  } catch {
    // Private-mode storage refusal: the notice is a nicety, never a correctness dependency.
  }
}

export function noteSessionEstablished(): void {
  sessionEstablished = true;
}

/**
 * Called by the API client on any 401. Only meaningful if we actually held a session — an
 * anonymous 401 is just the sign-in page asking who we are and being told nobody.
 */
/** Set once an elevation refusal has sent the page to sign-in; later 401s are ignored. */
let elevationBounced = false;

export function noteUnauthenticated(reason?: SessionRefusalReason): void {
  /*
   * `elevation_required` is NOT a lost session: the suite session is still valid, so the
   * re-ask below would answer 200, the session query would stay populated, every admin
   * read would retry into the same 401, and the screen would loop (seen on sandbox: ~40
   * retries per endpoint). The only remedy is a fresh sign-in, which mints the elevation
   * cookie — so record the notice and navigate to sign-in ONCE, as a full page load that
   * resets every query. Concurrent 401s from the same screen all land here; the flag makes
   * the first one the only one that navigates.
   */
  if (reason === 'elevation_required') {
    if (elevationBounced) return;
    elevationBounced = true;
    write('session-refused', reason);
    // END the suite session first: the sign-in page sends a still-signed-in person straight
    // back to the launcher, which would swallow the notice and leave the elevation missing.
    // Logout clears both cookies server-side; the full page load then lands on sign-in as
    // an anonymous visitor with the notice. A failed logout still navigates — sign-in is
    // the only way forward either way.
    void fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
      .catch(() => undefined)
      .finally(() => {
        window.location.assign(LOGIN_PATH);
      });
    return;
  }

  /*
   * A REASON IS RECORDED WHENEVER THE SERVER SENDS ONE, established session or not.
   *
   * `sessionEstablished` is module state, so a full page load resets it — and a reload with a
   * stale cookie is the ORDINARY way people meet a refusal (close the laptop, come back, hit
   * refresh). Gating on it there threw the reason away on exactly the path this feature was
   * built for, and there is no second chance: the server clears the cookie on the same
   * response that carries the reason, so every request after it is anonymous and reasonless.
   *
   * Reading it unconditionally leaks nothing. The SERVER attaches `reason` only to a cookie
   * it validly signed for a real account, so the reason's presence is itself the proof of
   * provenance — the client-side gate was duplicating a check already made, and better, one
   * step upstream.
   */
  if (reason !== undefined) write('session-refused', reason);

  // The reasonless notice and the re-ask still require that we actually held a session: an
  // anonymous 401 is just the sign-in page asking who we are and being told nobody.
  if (!sessionEstablished) return;
  sessionEstablished = false;
  if (reason === undefined) write('session-refused');
  // Re-ask the server who we are. It answers 401, the session query empties, and the guard
  // takes the person to sign-in — rather than leaving a screen whose every read now fails.
  onSessionLost?.();
}

/** Called when the person signs out deliberately — a different notice from a refusal. */
export function noteSignedOut(): void {
  sessionEstablished = false;
  write('signed-out');
}

/** The notice for a bounce, chosen by what the server said and what the client knew. */
export function bounceMessage(bounce: Bounce, strings: BounceStrings): string {
  if (bounce.kind === 'signed-out') return strings.signedOut;
  switch (bounce.reason) {
    case 'account_inactive':
      return strings.accountInactive;
    case 'signed_out_elsewhere':
      return strings.signedOutElsewhere;
    case 'password_changed':
      return strings.passwordChanged;
    case 'session_ended':
      return strings.sessionEnded;
    case 'elevation_required':
      return strings.elevationRequired;
    case null:
      // A refusal with no reason: the cookie never named a real account this service knows,
      // so nothing may be claimed about the account — only that the session is gone.
      return strings.expired;
  }
}

export interface BounceStrings {
  readonly signedOut: string;
  readonly accountInactive: string;
  readonly signedOutElsewhere: string;
  readonly passwordChanged: string;
  readonly sessionEnded: string;
  readonly elevationRequired: string;
  readonly expired: string;
}

/**
 * Read WITHOUT consuming. Reading and clearing in one call cannot be done from a render —
 * React invokes a state initializer more than once in development, and a self-consuming read
 * loses the value on the second pass. So the sign-in page peeks during render and clears in
 * an effect, which is idempotent.
 */
export function peekBounce(): Bounce | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  const [kind, reason] = raw.split(':', 2);
  if (kind === 'signed-out') return { kind, reason: null };
  if (kind !== 'session-refused') return null;
  const named = REFUSAL_REASONS.find((candidate) => candidate === reason) ?? null;
  return { kind, reason: named };
}

/** The notice shows once, on the next sign-in screen, and never again. */
export function clearBounceReason(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable — nothing was written either.
  }
}
